import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Ban, RotateCcw, ShieldCheck, RefreshCw, Search } from "lucide-react";

interface GuestIpRow {
  ip: string;
  spent_cents: number;
  budget_cents: number;
  blocked: boolean;
  request_count: number;
  distinct_user_count: number;
  first_seen_at: string;
  last_seen_at: string;
}

const formatCents = (c: number) => `${(c / 100).toFixed(2)} ($${(c / 100).toFixed(2)})`;
const formatDate = (iso: string) => new Date(iso).toLocaleString();

export const GuestIpUsage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState<GuestIpRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [pendingBudget, setPendingBudget] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("guest_ip_usage")
      .select("*")
      .order("last_seen_at", { ascending: false })
      .limit(500);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setRows((data as GuestIpRow[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const callAdmin = async (ip: string, action: string, extra?: Record<string, unknown>) => {
    setBusy(`${ip}:${action}`);
    try {
      const { data, error } = await supabase.functions.invoke("admin-guest-ip", {
        body: { ip, action, ...extra },
      });
      if (error || data?.error) {
        throw new Error(data?.error || error?.message || "Failed");
      }
      toast({ title: "Updated", description: `${action} on ${ip}` });
      await load();
    } catch (e: unknown) {
      toast({ title: "Error", description: (e instanceof Error ? e.message : "Request failed"), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const filtered = rows.filter(r => !filter || r.ip.includes(filter.trim()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Guest IP Usage</h2>
          <p className="text-muted-foreground text-sm">
            Per-IP free-trial spending. Default budget: 50¢. Block, reset, or override per IP.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter by IP…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="border border-border/50 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>IP</TableHead>
              <TableHead className="text-right">Spent ¢</TableHead>
              <TableHead className="text-right">Budget ¢</TableHead>
              <TableHead className="text-right">Remaining ¢</TableHead>
              <TableHead className="text-right">Reqs</TableHead>
              <TableHead className="text-right">Users</TableHead>
              <TableHead>First seen</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">No guest IPs yet.</TableCell></TableRow>
            )}
            {filtered.map((r) => {
              const remaining = Math.max(0, r.budget_cents - r.spent_cents);
              const overLimit = r.spent_cents >= r.budget_cents;
              return (
                <TableRow key={r.ip}>
                  <TableCell className="font-mono text-xs">{r.ip}</TableCell>
                  <TableCell className="text-right">{r.spent_cents}</TableCell>
                  <TableCell className="text-right">{r.budget_cents}</TableCell>
                  <TableCell className={`text-right ${overLimit ? "text-destructive" : ""}`}>{remaining}</TableCell>
                  <TableCell className="text-right">{r.request_count}</TableCell>
                  <TableCell className="text-right">{r.distinct_user_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(r.first_seen_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(r.last_seen_at)}</TableCell>
                  <TableCell>
                    {r.blocked ? (
                      <Badge variant="outline" className="border-destructive/50 bg-destructive/10 text-destructive">Blocked</Badge>
                    ) : overLimit ? (
                      <Badge variant="outline" className="border-yellow-500/50 bg-yellow-500/10 text-yellow-400">Over limit</Badge>
                    ) : (
                      <Badge variant="outline" className="border-emerald-500/50 bg-emerald-500/10 text-emerald-400">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      {r.blocked ? (
                        <Button size="sm" variant="outline" disabled={busy?.startsWith(r.ip)} onClick={() => callAdmin(r.ip, "unblock")}>
                          <ShieldCheck className="h-3.5 w-3.5 mr-1" />Unblock
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" disabled={busy?.startsWith(r.ip)} onClick={() => callAdmin(r.ip, "block")}>
                          <Ban className="h-3.5 w-3.5 mr-1" />Block
                        </Button>
                      )}
                      <Button size="sm" variant="outline" disabled={busy?.startsWith(r.ip)} onClick={() => callAdmin(r.ip, "reset")}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />Reset
                      </Button>
                      <Input
                        type="number"
                        min={0}
                        placeholder="¢"
                        className="h-8 w-20"
                        value={pendingBudget[r.ip] ?? ""}
                        onChange={(e) => setPendingBudget((p) => ({ ...p, [r.ip]: e.target.value }))}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy?.startsWith(r.ip) || !pendingBudget[r.ip]}
                        onClick={() => {
                          const v = parseInt(pendingBudget[r.ip] ?? "", 10);
                          if (Number.isFinite(v) && v >= 0) {
                            callAdmin(r.ip, "set_budget", { budget_cents: v }).then(() => {
                              setPendingBudget((p) => ({ ...p, [r.ip]: "" }));
                            });
                          }
                        }}
                      >
                        Set
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
